import React, { useEffect, useState } from "react";
import "./CreateProjectComponent.scss";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import CustomizedButton from "@components/ButtonConfirm/ButtonConfirm";
import {
  useCreateProjectMutation,
  useUpdateProjectMutation,
} from "store/project/project.api";
import { useSelector } from "react-redux";
import { useGetUsersBySupplierAndRoleQuery } from "store/user/userApi";
import { useGetSuppliersQuery } from "store/supplier/supplier.api"; // ✅ import query suppliers

const CreateProjectComponentAdmin = ({ onClose, project }) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);

  // ✅ récupérer le companyId courant
  const companyId = currentUser?.companyId._id;

  // ✅ hook suppliers par company
  const { data: suppliersData = [], isLoading: isLoadingSuppliers } =
    useGetSuppliersQuery(companyId, {
      skip: !companyId,
    });

  // ✅ state supplier sélectionné
  const [selectedSupplierId, setSelectedSupplierId] = useState(
    project?.supplierId || ""
  );

  // ✅ fetch collaborateurs seulement si supplier sélectionné
  const { data: supplierUsers = [], isLoading: isLoadingUsers } =
    useGetUsersBySupplierAndRoleQuery(selectedSupplierId, {
      skip: !selectedSupplierId,
    });

  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();

  // ✅ setup react-hook-form avec defaultValues
  const { register, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      title: project?.title || "",
      description: project?.description || "",
      startDate: project?.startDate
        ? new Date(project.startDate).toISOString().split("T")[0]
        : "",
      endDate: project?.endDate
        ? new Date(project.endDate).toISOString().split("T")[0]
        : "",
      supplierId: project?.supplierId || "",
      collaboratorId: project?.collaborator?._id || "",
      companyId: currentUser?.companyId._id,
    },
  });

  // ✅ Recharger les valeurs si `project` change (édition)
  useEffect(() => {
    if (project) {
      reset({
        title: project.title,
        description: project.description,
        startDate: project.startDate
          ? new Date(project.startDate).toISOString().split("T")[0]
          : "",
        endDate: project.endDate
          ? new Date(project.endDate).toISOString().split("T")[0]
          : "",
        supplierId: project.supplierId || "",
        collaboratorId: project.collaborator?._id || "",
        companyId: currentUser?.companyId._id,
      });
      setSelectedSupplierId(project.supplierId || "");
    }
  }, [project, reset]);

  const onSubmit = async (data) => {
    try {
      await createProject({
        ...data,
        supplierId: selectedSupplierId, // ✅ prendre le supplier choisi
      }).unwrap();
      toast.success(t("projects.form.create_success"));
      onClose();
    } catch {
      toast.error(t("projects.form.create_error"));
    }
  };

  const onUpdate = async (data) => {
    try {
      await updateProject({
        projectId: project._id,
        data: { ...data, supplierId: selectedSupplierId },
      }).unwrap();
      toast.success(t("projects.form.update_success"));
      onClose();
    } catch {
      toast.error(t("projects.form.update_error"));
    }
  };

  return (
    <>
      <div className="row-container ">
        {/* Title */}
        <div className="input-container">
          <div className="input-title">{t("projects.form.title")}</div>
          <input
            {...register("title", { required: "Le titre est requis" })}
            className="input-content"
            placeholder={t("projects.form.title_placeholder")}
          />
        </div>

        {/* Description */}
        <div className="input-container">
          <div className="input-title">{t("projects.form.description")}</div>
          <textarea
            {...register("description")}
            className="input-content"
            placeholder={t("projects.form.description_placeholder")}
          />
        </div>
      </div>
      <div className="row-container ">
        {/* Start Date */}
        <div className="input-container">
          <div className="input-title">{t("projects.form.startDate")}</div>
          <input
            type="date"
            {...register("startDate", {
              required: "La date de début est requise",
            })}
            className="input-content"
          />
        </div>

        {/* End Date */}
        <div className="input-container">
          <div className="input-title">{t("projects.form.endDate")}</div>
          <input
            type="date"
            {...register("endDate", {
              required: "La date de fin est requise",
            })}
            className="input-content"
          />
        </div>
      </div>
      {/* Supplier Select */}
      <div className="input-container">
        <div className="input-title">{t("projects.form.supplier")}</div>
        <select
          {...register("supplierId", { required: "Le supplier est requis" })}
          className="input-content"
          disabled={isLoadingSuppliers}
          value={selectedSupplierId}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedSupplierId(value);
            setValue("supplierId", value);
            setValue("collaboratorId", ""); // reset collaborator si changement supplier
          }}>
          <option value="">
            {isLoadingSuppliers
              ? t("projects.form.loading_suppliers")
              : t("projects.form.select_supplier")}
          </option>
          {suppliersData?.data?.map((supplier) => (
            <option key={supplier._id} value={supplier._id}>
              {supplier.name}
            </option>
          ))}
        </select>
      </div>

      {/* Collaborator Select - affiché seulement si supplier choisi */}
      {selectedSupplierId && (
        <div className="input-container">
          <div className="input-title">{t("projects.form.collaborator")}</div>
          <select
            {...register("collaboratorId", {
              required: "Le collaborateur est requis",
            })}
            className="input-content"
            disabled={isLoadingUsers}>
            <option value="">
              {isLoadingUsers
                ? t("projects.form.loading_users")
                : t("projects.form.select_collaborator")}
            </option>
            {supplierUsers?.data?.map((user) => (
              <option key={user._id} value={user._id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Button */}
      <CustomizedButton
        creatingText={t(
          project ? "projects.form.updating" : "projects.form.creating"
        )}
        finalizeText={t(
          project ? "projects.form.update" : "projects.form.submit"
        )}
        handleCreate={() => {
          if (project) handleSubmit(onUpdate)();
          else handleSubmit(onSubmit)();
        }}
        isCreating={isCreating || isUpdating}
        className="btn w-100"
      />
    </>
  );
};

export default CreateProjectComponentAdmin;
